import { getAuthToken, getPageAuthSession, getStoredSession, isStoredSessionCurrent } from './auth';
import { normalizePrintPoints } from './printPoints';

export const GENERAL_SETTINGS_KEY = 'configuracoes-geral';
export const PRINTER_SETTINGS_KEY = 'configuracoes-impressao';
export const PRINTERS_CACHE_KEY = 'cibd-printers-cache';
export const AUTO_PRINT_KEY = 'autoPrint';
export const AUTO_ACCEPT_KEY = 'autoAccept';
export const CIBD_STATION_PROFILE_KEY = 'brutos-cibd-station-v1';

const cleanText = (value) => String(value || '').trim();
const cleanBaseUrl = (value) => cleanText(value).replace(/\/+$/, '');
const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
};

const normalizePrinters = (value) => Array.isArray(value)
  ? value.filter((printer) => printer && cleanText(printer.id || printer.name || printer.printerId))
  : [];

const tenantConnectionToken = (settings = {}) => cleanText(settings.cibdToken || settings.bridgeToken);
const extractPrinterBindings = (settings = {}) => Object.fromEntries(
  normalizePrintPoints(settings)
    .map((point) => [cleanText(point.id), cleanText(point.printerId)])
    .filter(([pointId, printerId]) => pointId && printerId)
);

const normalizeBindings = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .map(([pointId, printerId]) => [cleanText(pointId), cleanText(printerId)])
    .filter(([pointId, printerId]) => pointId && printerId)
    .slice(0, 5));
};

const normalizeStationProfile = (value, baseUrl) => {
  const expectedBaseUrl = cleanBaseUrl(baseUrl);
  if (!value || typeof value !== 'object' || value.version !== 1
    || cleanBaseUrl(value.baseUrl) !== expectedBaseUrl) return null;
  return {
    version: 1,
    baseUrl: expectedBaseUrl,
    token: cleanText(value.token),
    printers: normalizePrinters(value.printers),
    defaultPrinter: cleanText(value.defaultPrinter),
    bindings: normalizeBindings(value.bindings),
    mode: value.mode === 'explicit' ? 'explicit' : 'automatic',
  };
};

export const getTenantSettingsSession = () => {
  // Initialize the page pin once; an invalidated page must not adopt a new login.
  const token = getAuthToken();
  if (!token) return null;
  const session = getPageAuthSession() || getStoredSession();
  return session?.token === token ? session : null;
};

export const getTenantStorageKey = (baseKey, session = getTenantSettingsSession()) => {
  const companyId = String(session?.user?.companyId || '').trim();
  const token = String(session?.token || '').trim();
  const pageToken = getAuthToken();
  const pageSession = getPageAuthSession();
  // A 401/payment invalidation may deliberately preserve localStorage. Both
  // the live page pin and persisted session must still authorize this caller.
  if (!baseKey || !companyId || !token || !pageToken || pageToken !== token
    || String(pageSession?.token || '').trim() !== token
    || String(pageSession?.user?.companyId || '').trim() !== companyId
    || !isStoredSessionCurrent(session)) return '';
  return `${baseKey}:tenant:${companyId}`;
};

export const readTenantLocalJson = (baseKey, fallback, session = getTenantSettingsSession()) => {
  const key = getTenantStorageKey(baseKey, session);
  if (!key) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeTenantLocalJson = (baseKey, value, session = getTenantSettingsSession()) => {
  const key = getTenantStorageKey(baseKey, session);
  if (!key) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

// CIBD is installed on the workstation. Its connection belongs to the current
// browser profile/origin and can be reused across authenticated tenants. Physical
// bindings become station defaults; tenant snapshots keep their own overrides.
// Logical point names, layout and automation remain tenant-scoped.
export const resolveCibdStationConnection = ({
  baseUrl,
  session = getTenantSettingsSession(),
  tenantSettings,
} = {}) => {
  if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, session)) return null;
  const normalizedBaseUrl = cleanBaseUrl(baseUrl);
  if (!normalizedBaseUrl) return null;
  const scopedSettings = tenantSettings ?? readTenantLocalJson(PRINTER_SETTINGS_KEY, {}, session);
  const scopedToken = tenantConnectionToken(scopedSettings);
  const scopedBindings = extractPrinterBindings(scopedSettings);
  const scopedPrinters = normalizePrinters(readTenantLocalJson(PRINTERS_CACHE_KEY, [], session));
  let profile = normalizeStationProfile(readJson(CIBD_STATION_PROFILE_KEY, null), normalizedBaseUrl);

  if (!profile) {
    // The old global printer key represented this same browser workstation.
    // Under the station contract only its physical fields are recoverable;
    // tenant names, registry, layout and automation are never imported.
    const legacyMachineSettings = readJson(PRINTER_SETTINGS_KEY, {});
    const legacyToken = tenantConnectionToken(legacyMachineSettings);
    const legacyBindings = extractPrinterBindings(legacyMachineSettings);
    const legacyPrinters = normalizePrinters(readJson(PRINTERS_CACHE_KEY, []));
    const hasScopedPhysical = Boolean(scopedToken || scopedPrinters.length || Object.keys(scopedBindings).length);
    const token = hasScopedPhysical ? scopedToken : legacyToken;
    const bindings = hasScopedPhysical ? scopedBindings : legacyBindings;
    const printers = hasScopedPhysical ? scopedPrinters : legacyPrinters;
    const defaultPrinter = bindings.caixa || cleanText(
      (hasScopedPhysical ? scopedSettings : legacyMachineSettings).printerId
      || (hasScopedPhysical ? scopedSettings : legacyMachineSettings).printerName
    );
    if (token || printers.length || Object.keys(bindings).length) {
      profile = {
        version: 1,
        baseUrl: normalizedBaseUrl,
        token,
        printers,
        defaultPrinter,
        bindings,
        mode: 'automatic',
      };
      try {
        window.localStorage.setItem(CIBD_STATION_PROFILE_KEY, JSON.stringify(profile));
      } catch {
        // Existing tenant settings stay authoritative when station storage fails.
      }
    }
  }

  // An automatically promoted profile must never replace a divergent existing
  // tenant token during business hours. Explicit station configuration wins.
  const useScopedFallback = profile?.mode !== 'explicit' && scopedToken && scopedToken !== profile?.token;
  const token = useScopedFallback ? scopedToken : profile ? profile.token : scopedToken;
  const printers = useScopedFallback && scopedPrinters.length ? scopedPrinters : profile?.printers || scopedPrinters;
  const defaultPrinter = useScopedFallback
    ? cleanText(scopedSettings.printerId || scopedSettings.printerName)
    : profile?.defaultPrinter || '';
  return {
    baseUrl: normalizedBaseUrl,
    token,
    printers,
    defaultPrinter,
    bindings: { ...(profile?.bindings || {}), ...scopedBindings },
    mode: useScopedFallback ? 'tenant-fallback' : profile?.mode || 'default',
    fingerprint: `${normalizedBaseUrl}\u0000${token}\u0000${defaultPrinter}\u0000${JSON.stringify(Object.entries({ ...(profile?.bindings || {}), ...scopedBindings }).sort())}`,
  };
};

export const isCibdStationConnectionCurrent = (connection, options = {}) => {
  if (!connection) return false;
  const current = resolveCibdStationConnection(options);
  return Boolean(current && current.fingerprint === connection.fingerprint);
};

export const writeCibdStationProfile = ({
  baseUrl,
  token,
  printers = [],
  defaultPrinter = '',
  bindings = {},
  mode = 'explicit',
  session = getTenantSettingsSession(),
} = {}) => {
  if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, session)) return false;
  const normalizedBaseUrl = cleanBaseUrl(baseUrl);
  if (!normalizedBaseUrl) return false;
  try {
    window.localStorage.setItem(CIBD_STATION_PROFILE_KEY, JSON.stringify({
      version: 1,
      baseUrl: normalizedBaseUrl,
      token: cleanText(token),
      printers: normalizePrinters(printers),
      defaultPrinter: cleanText(defaultPrinter),
      bindings: normalizeBindings(bindings),
      mode: mode === 'explicit' ? 'explicit' : 'automatic',
    }));
    return true;
  } catch {
    return false;
  }
};

export const updateCibdStationDiscovery = ({ connection, printers, defaultPrinter, session } = {}) => {
  if (!isCibdStationConnectionCurrent(connection, { baseUrl: connection?.baseUrl, session })) return false;
  if (connection.mode === 'tenant-fallback') {
    return writeTenantLocalJson(PRINTERS_CACHE_KEY, normalizePrinters(printers), session);
  }
  const current = normalizeStationProfile(readJson(CIBD_STATION_PROFILE_KEY, null), connection.baseUrl);
  return writeCibdStationProfile({
    baseUrl: connection.baseUrl,
    token: current?.token ?? connection.token,
    printers,
    defaultPrinter,
    bindings: current?.bindings || connection.bindings,
    mode: current?.mode || 'automatic',
    session,
  });
};

export const applyCibdStationBindings = (settings = {}, connection = null) => {
  const bindings = normalizeBindings(connection?.bindings);
  const points = normalizePrintPoints(settings).map((point) => ({
    ...point,
    printerId: cleanText(point.printerId || bindings[point.id]
      || (point.id === 'caixa' ? connection?.defaultPrinter : '')),
  }));
  return {
    ...settings,
    printerId: points.find((point) => point.id === 'caixa')?.printerId
      || cleanText(settings.printerId || settings.printerName || connection?.defaultPrinter),
    printPoints: points,
  };
};

// New tenant snapshots do not duplicate station credentials. Existing tenant
// credentials are preserved verbatim as a rollback/fallback source.
export const writeTenantPrinterSettings = (value, session = getTenantSettingsSession()) => {
  const key = getTenantStorageKey(PRINTER_SETTINGS_KEY, session);
  if (!key) return false;
  const existing = readJson(key, {});
  const next = { ...(value || {}) };
  if (Object.prototype.hasOwnProperty.call(existing, 'cibdToken')) next.cibdToken = existing.cibdToken;
  else delete next.cibdToken;
  if (Object.prototype.hasOwnProperty.call(existing, 'bridgeToken')) next.bridgeToken = existing.bridgeToken;
  else delete next.bridgeToken;
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
};
